import { Injectable } from '@angular/core';
import { CurrentContextService } from './current-context.service';

import enTranslations from 'assets/languages/translations/en.json';
import svTranslations from 'assets/languages/translations/sv.json';
import noTranslations from 'assets/languages/translations/no.json';
import daTranslations from 'assets/languages/translations/da.json';
import deTranslations from 'assets/languages/translations/de.json';
import esTranslations from 'assets/languages/translations/es.json';
import fiTranslations from 'assets/languages/translations/fi.json';
import frTranslations from 'assets/languages/translations/fr.json';
import huTranslations from 'assets/languages/translations/hu.json';
import itTranslations from 'assets/languages/translations/it.json';
import nlTranslations from 'assets/languages/translations/nl.json';
import plTranslations from 'assets/languages/translations/pl.json';
import ptTranslations from 'assets/languages/translations/pt.json';
import roTranslations from 'assets/languages/translations/ro.json';
import ukTranslations from 'assets/languages/translations/uk.json';

export type TranslationValue = string | { [key: string]: TranslationValue };

export interface TranslationObject {
  [key: string]: TranslationValue;
}

@Injectable({
  providedIn: 'root',
})
export class AccountLanguageService {
  private translations: Record<string, TranslationObject> = {
    en: enTranslations,
    sv: svTranslations,
    no: noTranslations,
    da: daTranslations,
    de: deTranslations,
    es: esTranslations,
    fi: fiTranslations,
    fr: frTranslations,
    hu: huTranslations,
    it: itTranslations,
    nl: nlTranslations,
    pl: plTranslations,
    pt: ptTranslations,
    ro: roTranslations,
    uk: ukTranslations,
  };

  private currentLanguage = 'en';

  constructor(private currentContextService: CurrentContextService) {
    this.currentContextService.currentContextBS.subscribe((context) => {
      if (context?.account?.language) {
        this.currentLanguage = context.account.language;
      }
    });
  }

  public getCurrentLanguage(): string {
    return this.currentLanguage;
  }

  public setLanguage(languageCode: string): void {
    this.currentLanguage = languageCode || 'en';
  }

  /**
   * Translate a key using dot notation (e.g., 'stepNavigation.nextButtonText').
   * Falls back to English if translation is not found in the current language.
   *
   * @param key Translation key
   * @returns Translated text or the key itself if translation not found
   */
  public translate(key: string): string {
    if (!key) {
      return '';
    }

    let translation = this.getNestedTranslation(this.translations[this.currentLanguage], key);

    if (translation === undefined && this.currentLanguage !== 'en') {
      translation = this.getNestedTranslation(this.translations['en'], key);
    }

    if (translation === undefined || typeof translation !== 'string') {
      return key;
    }

    return translation;
  }

  private getNestedTranslation(translations: TranslationObject | undefined, key: string): TranslationValue | undefined {
    if (!translations) {
      return undefined;
    }

    const keys = key.split('.');
    let result: TranslationValue = translations;

    for (const k of keys) {
      if (result && typeof result === 'object' && !Array.isArray(result) && k in result) {
        result = result[k];
      } else {
        return undefined;
      }
    }

    return result;
  }

  public hasTranslation(key: string): boolean {
    const translation = this.getNestedTranslation(this.translations[this.currentLanguage], key);
    if (translation !== undefined) {
      return true;
    }
    if (this.currentLanguage !== 'en') {
      const fallback = this.getNestedTranslation(this.translations['en'], key);
      return fallback !== undefined;
    }
    return false;
  }

  public getTranslations(languageCode?: string): TranslationObject {
    const lang = languageCode || this.currentLanguage;
    return this.translations[lang] || this.translations['en'];
  }
}
